// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-22) — the headset's real zoom, AYNI 3D's way: the
 * right stick, aimed at a place, makes that place grow and everything else
 * smaller, while the globe keeps its size in the room. The left stick still
 * sets the size (vrInteraction.ts). Plan: docs/AYNI_XR_HEADSET_ZOOM_PLAN.md.
 *
 * A point at angle α' from the focus is drawn at α = 2·atan(m·tan(α'/2)):
 * m times bigger near the focus, the rest of the Earth squeezed toward the
 * far side of the ball. The warp is done per pixel in the shaders
 * ({@link FOCUS_ZOOM_GLSL}); the sphere's geometry never changes.
 *
 * Three spaces, all in the globe mesh's own frame (THREE's sphere, the
 * frame `latLonToGlobeLocal` uses):
 * - **display**: a point on the ball, where something is drawn;
 * - **content**: the map before the warp, around the display focus;
 * - **source**: the place on Earth. `content = C · source`, C a rotation
 *   that lets a drag move the map under a still magnified spot (AYNI 3D's
 *   drag pans by an angle divided by the zoom).
 *
 * At m = 1 the warp is the identity and C is folded into the globe's own
 * rotation, so the state is always plain there.
 */

import { CITY_DEEP_MAX_ZOOM, CITY_MAX_ZOOM, cityPackAt, inDeepBox } from './cityCloseup'
import { FINE_DATA_MAX_ZOOM, GLOBE_MAX_ZOOM } from './globeZoomCeiling'

export type Vec3 = [number, number, number]
/** A rotation as a unit quaternion, x y z w (THREE's order). */
export type Quat = [number, number, number, number]

/** The deepest zoom anywhere (zoom 16, Barcelona and Rotterdam). */
export const MAX_FOCUS_MAGNIFICATION = magnificationOf(CITY_DEEP_MAX_ZOOM)
/** Under this the zoom counts as off (rounding after a fold). */
const ZOOM_EPSILON = 1e-6

/** AYNI 3D's zoom level for a magnification (`zoom-ceiling.ts`: m = 2^(z−3)). */
export function zoomLevelOf(m: number): number {
  return 3 + Math.log2(Math.max(1, m))
}

/** The magnification for an AYNI 3D zoom level. */
export function magnificationOf(zoom: number): number {
  return 2 ** (zoom - 3)
}

/**
 * How far in the zoom may go with the focus on a place: the browser's
 * ceilings (globeZoomCeiling.ts, cityCloseup.ts) as magnifications — 8
 * everywhere, 9.5 with a dataset finer than 200 m, 14 over a city pack and
 * 16 over its deep box. Never past 10 outside the packs (owner rule).
 */
export function focusZoomCeiling(lat: number, lon: number, fineData: boolean): number {
  const pack = cityPackAt(lon, lat)
  let zoom = fineData ? FINE_DATA_MAX_ZOOM : GLOBE_MAX_ZOOM
  if (pack) zoom = Math.max(zoom, inDeepBox(pack, lon, lat) ? CITY_DEEP_MAX_ZOOM : CITY_MAX_ZOOM)
  return magnificationOf(zoom)
}

// --- Vector and rotation helpers (plain arrays, so this file needs no THREE) ---

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}

function normalize(v: Vec3): Vec3 {
  const l = Math.hypot(v[0], v[1], v[2])
  return l > 0 ? [v[0] / l, v[1] / l, v[2] / l] : [1, 0, 0]
}

/** The angle between two unit vectors, accurate near 0 and π. */
export function angleBetween(a: Vec3, b: Vec3): number {
  const c = cross(a, b)
  return Math.atan2(Math.hypot(c[0], c[1], c[2]), dot(a, b))
}

export function applyQuat(q: Quat, v: Vec3): Vec3 {
  const [qx, qy, qz, qw] = q
  const [x, y, z] = v
  const ix = qw * x + qy * z - qz * y
  const iy = qw * y + qz * x - qx * z
  const iz = qw * z + qx * y - qy * x
  const iw = -qx * x - qy * y - qz * z
  return [
    ix * qw + iw * -qx + iy * -qz - iz * -qy,
    iy * qw + iw * -qy + iz * -qx - ix * -qz,
    iz * qw + iw * -qz + ix * -qy - iy * -qx,
  ]
}

/** a · b: b first, then a. */
export function mulQuat(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a
  const [bx, by, bz, bw] = b
  return normalizeQuat([
    ax * bw + aw * bx + ay * bz - az * by,
    ay * bw + aw * by + az * bx - ax * bz,
    az * bw + aw * bz + ax * by - ay * bx,
    aw * bw - ax * bx - ay * by - az * bz,
  ])
}

export function invertQuat(q: Quat): Quat {
  return [-q[0], -q[1], -q[2], q[3]]
}

function normalizeQuat(q: Quat): Quat {
  const l = Math.hypot(q[0], q[1], q[2], q[3]) || 1
  return [q[0] / l, q[1] / l, q[2] / l, q[3] / l]
}

function axisAngle(axis: Vec3, angle: number): Quat {
  const s = Math.sin(angle / 2)
  return [axis[0] * s, axis[1] * s, axis[2] * s, Math.cos(angle / 2)]
}

/** The shortest rotation taking unit vector `from` onto `to` (THREE's setFromUnitVectors). */
export function quatFromUnitVectors(from: Vec3, to: Vec3): Quat {
  let r = dot(from, to) + 1
  let q: Quat
  if (r < 1e-12) {
    r = 0
    q = Math.abs(from[0]) > Math.abs(from[2]) ? [-from[1], from[0], 0, r] : [0, -from[2], from[1], r]
  } else {
    const c = cross(from, to)
    q = [c[0], c[1], c[2], r]
  }
  return normalizeQuat(q)
}

/** The unit vector `angle` along the great circle from `from` toward `toward`. */
function alongGreatCircle(from: Vec3, toward: Vec3, angle: number): Vec3 {
  const c = dot(from, toward)
  let t: Vec3 = [toward[0] - c * from[0], toward[1] - c * from[1], toward[2] - c * from[2]]
  const l = Math.hypot(t[0], t[1], t[2])
  if (l < 1e-12) {
    // `toward` is the focus or its opposite: any direction away from it will do.
    t = normalize(cross(from, Math.abs(from[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0]))
  } else {
    t = [t[0] / l, t[1] / l, t[2] / l]
  }
  const ca = Math.cos(angle)
  const sa = Math.sin(angle)
  return [ca * from[0] + sa * t[0], ca * from[1] + sa * t[1], ca * from[2] + sa * t[2]]
}

/** A place as a unit vector in the globe's frame (latLonToGlobeLocal at radius 1). */
export function latLonToDir(lat: number, lon: number): Vec3 {
  const la = lat * Math.PI / 180
  const lo = lon * Math.PI / 180
  return [Math.cos(la) * Math.cos(lo), Math.sin(la), -Math.cos(la) * Math.sin(lo)]
}

export function dirToLatLon(v: Vec3): { lat: number; lon: number } {
  const n = normalize(v)
  return {
    lat: Math.asin(Math.max(-1, Math.min(1, n[1]))) * 180 / Math.PI,
    lon: Math.atan2(-n[2], n[0]) * 180 / Math.PI,
  }
}

/** The sphere UV (u east from the antimeridian, v north from the south pole) of a place. */
export function latLonToUv(lat: number, lon: number): { x: number; y: number } {
  return { x: lon / 360 + 0.5, y: lat / 180 + 0.5 }
}

// --- The zoom state ---

export interface FocusZoomState {
  /** Magnification, 1 = off. */
  m: number
  /** The magnified spot on the ball (display space, unit vector). */
  fd: Vec3
  /** content = c · source. Identity whenever m = 1. */
  c: Quat
}

const IDENTITY: Quat = [0, 0, 0, 1]

export function plainZoom(): FocusZoomState {
  return { m: 1, fd: [1, 0, 0], c: [...IDENTITY] }
}

export function isZoomed(state: FocusZoomState): boolean {
  return state.m > 1 + ZOOM_EPSILON
}

/** Where a content point is drawn: the warp itself. */
export function contentToDisplay(state: FocusZoomState, content: Vec3): Vec3 {
  if (!isZoomed(state)) return content
  const a = angleBetween(state.fd, content)
  return alongGreatCircle(state.fd, content, 2 * Math.atan(state.m * Math.tan(a / 2)))
}

/** The content point drawn at a display point: the warp undone. */
export function displayToContent(state: FocusZoomState, display: Vec3): Vec3 {
  if (!isZoomed(state)) return display
  const a = angleBetween(state.fd, display)
  return alongGreatCircle(state.fd, display, 2 * Math.atan(Math.tan(a / 2) / state.m))
}

/** The place on Earth drawn at a point of the ball (both in the globe's frame). */
export function displayToSource(state: FocusZoomState, display: Vec3): Vec3 {
  if (!isZoomed(state)) return display
  return applyQuat(invertQuat(state.c), displayToContent(state, display))
}

/** Where a place on Earth is drawn on the ball. */
export function sourceToDisplay(state: FocusZoomState, source: Vec3): Vec3 {
  if (!isZoomed(state)) return source
  return contentToDisplay(state, applyQuat(state.c, source))
}

/** The place under the focus: the centre of what is magnified. */
export function focusSource(state: FocusZoomState): Vec3 {
  return applyQuat(invertQuat(state.c), state.fd)
}

/**
 * Change the zoom by `factor`, keeping the place drawn at `anchor` (a
 * display point; null: the focus) where it is. From 1× the focus first
 * moves to the anchor. The zoom stays between 1 and `ceiling`; a zoom
 * already above a ceiling that dropped may come down but not go further up.
 *
 * Returns the rotation to fold into the globe's own rotation when the zoom
 * comes back to 1 (`globe.quaternion.multiply(fold)`), else null.
 */
export function zoomBy(state: FocusZoomState, factor: number, anchor: Vec3 | null, ceiling: number): Quat | null {
  const m0 = state.m
  const m2 = Math.max(1, factor > 1 ? Math.min(m0 * factor, Math.max(ceiling, m0)) : m0 * factor)
  if (!isZoomed(state)) {
    if (m2 <= 1 + ZOOM_EPSILON) return null
    if (anchor) state.fd = normalize(anchor)
    state.c = [...IDENTITY]
  } else if (anchor) {
    const a = angleBetween(state.fd, anchor)
    if (a > 1e-9 && a < Math.PI - 1e-6) {
      // Slide the map along the great circle through the focus and the
      // anchor so the anchored place lands at the same angle under the new
      // magnification: it stays under the ray.
      const content = displayToContent(state, anchor)
      const before = angleBetween(state.fd, content)
      const after = 2 * Math.atan(Math.tan(a / 2) / m2)
      const axis = cross(content, state.fd)
      if (Math.hypot(axis[0], axis[1], axis[2]) > 1e-12) {
        state.c = mulQuat(axisAngle(normalize(axis), before - after), state.c)
      }
    }
  }
  state.m = m2
  if (state.m <= 1 + ZOOM_EPSILON) {
    const fold = state.c
    state.m = 1
    state.c = [...IDENTITY]
    return fold
  }
  return null
}

/**
 * While zoomed, a drag moves the map under the magnified spot: `source`
 * (the place grabbed) goes to wherever is now drawn at `display`.
 */
export function panTo(state: FocusZoomState, source: Vec3, display: Vec3): void {
  if (!isZoomed(state)) return
  const target = displayToContent(state, display)
  const current = applyQuat(state.c, source)
  state.c = mulQuat(quatFromUnitVectors(normalize(current), normalize(target)), state.c)
}

/**
 * The zoom brought back to 1: the rotation to fold into the globe's own
 * (`globe.quaternion.multiply(fold)`), or null when it was not zoomed.
 */
export function resetZoom(state: FocusZoomState): Quat | null {
  if (!isZoomed(state)) {
    state.m = 1
    state.c = [...IDENTITY]
    return null
  }
  const fold = state.c
  state.m = 1
  state.c = [...IDENTITY]
  return fold
}

/** The shaders' view of the state ({@link FOCUS_ZOOM_GLSL}). */
export interface FocusZoomShaderValues {
  m: number
  fd: Vec3
  /** The source's east and north at the focus, carried into content space. */
  e: Vec3
  n: Vec3
  /** The place under the focus: lat, lon in radians, then in degrees. */
  src: [number, number, number, number]
}

export function shaderValues(state: FocusZoomState): FocusZoomShaderValues {
  const zoomed = isZoomed(state)
  const s = zoomed ? focusSource(state) : state.fd
  const { lat, lon } = dirToLatLon(s)
  const la = lat * Math.PI / 180
  const lo = lon * Math.PI / 180
  const east: Vec3 = [-Math.sin(lo), 0, -Math.cos(lo)]
  const north: Vec3 = [-Math.sin(la) * Math.cos(lo), Math.cos(la), Math.sin(la) * Math.sin(lo)]
  return {
    m: zoomed ? state.m : 1,
    fd: [...state.fd],
    e: applyQuat(state.c, east),
    n: applyQuat(state.c, north),
    src: [la, lo, lat, lon],
  }
}

/**
 * The warp in GLSL, for every material drawn on the globe: `zoomLatLon(uv)`
 * gives the place (lat, lon in degrees) drawn at a sphere UV — the UV after
 * the inner face's east-west mirror — and `zoomUv` the same as a UV. Off
 * (uZoomM ≤ 1) they return the UV's own place. `zoomTexture` samples a
 * whole-Earth picture there without a seam where the longitude wraps, and
 * `zoomFootprint` is sparseSampling's footprint without that jump.
 *
 * The source is the point at that distance and bearing from the place in
 * focus, written from small terms only, so a 32-bit float keeps it to
 * centimetres near the focus. Further out (over 300 km) latitude comes from
 * the plain formula, where the magnification is near 1 and a float's
 * rounding no longer shows.
 */
export const FOCUS_ZOOM_GLSL = /* glsl */ `
  uniform float uZoomM;
  uniform vec3 uZoomFd;
  uniform vec3 uZoomE;
  uniform vec3 uZoomN;
  uniform vec4 uZoomSrc;

  vec2 zoomLatLon(vec2 uv) {
    float latD = (uv.y - 0.5) * 180.0;
    float lonD = (uv.x - 0.5) * 360.0;
    if (uZoomM <= 1.0) return vec2(latD, lonD);
    float la = radians(latD);
    float lo = radians(lonD);
    vec3 d = vec3(cos(la) * cos(lo), sin(la), -cos(la) * sin(lo));
    float cosA = dot(d, uZoomFd);
    vec3 t = d - cosA * uZoomFd;
    float sinA = length(t);
    float a = atan(sinA, cosA);
    float a2 = 2.0 * atan(tan(0.5 * a) / uZoomM);
    vec2 b = sinA > 1e-9 ? vec2(dot(t, uZoomE), dot(t, uZoomN)) / sinA : vec2(0.0, 1.0);
    float lat0 = uZoomSrc.x;
    float s0 = sin(lat0);
    float c0 = cos(lat0);
    float h = sin(0.5 * a2);
    // sin(lat) − sin(lat0), from small terms only.
    float k = c0 * sin(a2) * b.y - 2.0 * s0 * h * h;
    float lat;
    if (a2 < 0.05) {
      // lat − lat0 from sin(Δ/2) = k / (2 cos(lat0 + Δ/2)), a few steps.
      float dl = k / max(c0, 1e-4);
      for (int i = 0; i < 4; i++) dl = 2.0 * asin(clamp(k / (2.0 * max(cos(lat0 + 0.5 * dl), 1e-4)), -1.0, 1.0));
      lat = uZoomSrc.z + degrees(dl);
    } else {
      lat = degrees(asin(clamp(s0 + k, -1.0, 1.0)));
    }
    float dlon = atan(b.x * sin(a2) * c0, c0 * c0 - 2.0 * h * h - s0 * k);
    float lon = uZoomSrc.w + degrees(dlon);
    lon -= 360.0 * floor((lon + 180.0) / 360.0);
    return vec2(lat, lon);
  }

  vec2 zoomUv(vec2 uv, vec2 ll) {
    return uZoomM <= 1.0 ? uv : vec2(ll.y / 360.0 + 0.5, ll.x / 180.0 + 0.5);
  }

  vec2 zoomWrapGrad(vec2 g) {
    g.x -= floor(g.x + 0.5);
    return g;
  }

  vec4 zoomTexture(sampler2D s, vec2 uv) {
    if (uZoomM <= 1.0) return texture(s, uv);
    return textureGrad(s, uv, zoomWrapGrad(dFdx(uv)), zoomWrapGrad(dFdy(uv)));
  }

  vec2 zoomFootprint(vec2 uv) {
    return max(abs(zoomWrapGrad(dFdx(uv))), abs(zoomWrapGrad(dFdy(uv))));
  }
`

/**
 * The close-up Earth in GLSL (the Earth material and the secondary globes):
 * three tile windows around the focus (vrDetailTiles.ts), finest first,
 * then the whole-Earth picture. A window is (west longitude in degrees, its
 * north edge as Web Mercator y, windows per degree, windows per Mercator
 * unit); z ≤ 0 marks a level that is off. Needs {@link FOCUS_ZOOM_GLSL}.
 */
export const DETAIL_TILES_GLSL = /* glsl */ `
  uniform float uDetailOn;
  uniform sampler2D uDetail0;
  uniform sampler2D uDetail1;
  uniform sampler2D uDetail2;
  uniform vec4 uDetailWin0;
  uniform vec4 uDetailWin1;
  uniform vec4 uDetailWin2;

  vec4 detailAt(sampler2D s, vec4 w, float lon, float mercY) {
    if (w.z <= 0.0) return vec4(0.0);
    float dl = lon - w.x;
    dl -= 360.0 * floor(dl / 360.0);
    vec2 p = vec2(dl * w.z, (w.y - mercY) * w.w);
    if (p.x < 0.0 || p.x > 1.0 || p.y < 0.0 || p.y > 1.0) return vec4(0.0);
    return texture(s, vec2(p.x, 1.0 - p.y));
  }

  vec4 zoomEarth(sampler2D baseMap, vec2 gUv, vec2 ll) {
    if (uDetailOn > 0.5 && uZoomM > 1.0) {
      float mercY = log(tan(0.7853981634 + 0.5 * radians(clamp(ll.x, -85.05, 85.05))));
      vec4 c = detailAt(uDetail0, uDetailWin0, ll.y, mercY);
      if (c.a > 0.5) return vec4(c.rgb / c.a, 1.0);
      c = detailAt(uDetail1, uDetailWin1, ll.y, mercY);
      if (c.a > 0.5) return vec4(c.rgb / c.a, 1.0);
      c = detailAt(uDetail2, uDetailWin2, ll.y, mercY);
      if (c.a > 0.5) return vec4(c.rgb / c.a, 1.0);
    }
    return zoomTexture(baseMap, gUv);
  }
`

/**
 * The uniforms every globe material shares, as plain values THREE accepts
 * (arrays for vectors). One set per page: the headset has one zoom.
 */
export const focusZoomUniforms = {
  uZoomM: { value: 1 },
  uZoomFd: { value: [1, 0, 0] as number[] },
  uZoomE: { value: [0, 0, -1] as number[] },
  uZoomN: { value: [0, 1, 0] as number[] },
  uZoomSrc: { value: [0, 0, 0, 0] as number[] },
}

/** The close-up tiles' uniforms (set by vrDetailTiles.ts; samplers null until it runs). */
export const detailTileUniforms = {
  uDetailOn: { value: 0 },
  uDetail0: { value: null as unknown },
  uDetail1: { value: null as unknown },
  uDetail2: { value: null as unknown },
  uDetailWin0: { value: [0, 0, 0, 0] as number[] },
  uDetailWin1: { value: [0, 0, 0, 0] as number[] },
  uDetailWin2: { value: [0, 0, 0, 0] as number[] },
}

// --- The page's zoom ---

let current: FocusZoomState = plainZoom()
let version = 0

/** The headset's zoom (read-only by convention; change it through the functions below). */
export function getFocusZoom(): FocusZoomState {
  return current
}

/** Bumped whenever the zoom changes, for drawings that follow it (pins). */
export function focusZoomVersion(): number {
  return version
}

/** Write the zoom into the shared uniforms; call after changing it. */
export function commitFocusZoom(): void {
  const v = shaderValues(current)
  focusZoomUniforms.uZoomM.value = v.m
  focusZoomUniforms.uZoomFd.value = v.fd
  focusZoomUniforms.uZoomE.value = v.e
  focusZoomUniforms.uZoomN.value = v.n
  focusZoomUniforms.uZoomSrc.value = v.src
  version++
}

/** Replace the zoom (a room's lead, a test). */
export function setFocusZoom(state: FocusZoomState): void {
  current = { m: Math.max(1, state.m), fd: normalize(state.fd), c: normalizeQuat(state.c) }
  if (!isZoomed(current)) current.c = [...IDENTITY]
  commitFocusZoom()
}

/** The page's zoom back to 1 (a new session). */
export function clearFocusZoom(): void {
  current = plainZoom()
  commitFocusZoom()
}

/** The place drawn at a sphere UV of the ball, as a UV (taps, pins, the value under the ray). */
export function displayUvToSourceUv(uv: { x: number; y: number }): { x: number; y: number } {
  if (!isZoomed(current)) return { x: uv.x, y: uv.y }
  const { lat, lon } = dirToLatLon(displayToSource(current, latLonToDir((uv.y - 0.5) * 180, (uv.x - 0.5) * 360)))
  return latLonToUv(lat, lon)
}

/** The display point (unit vector, globe frame) of a sphere UV. */
export function uvToDisplayDir(uv: { x: number; y: number }): Vec3 {
  return latLonToDir((uv.y - 0.5) * 180, (uv.x - 0.5) * 360)
}

/** Where a place is drawn on the ball, in the globe's frame at `radius` (pins). */
export function zoomedGlobeLocal(lat: number, lon: number, radius: number): { x: number; y: number; z: number } {
  const d = sourceToDisplay(current, latLonToDir(lat, lon))
  return { x: d[0] * radius, y: d[1] * radius, z: d[2] * radius }
}
